/**
 * ContactSection.jsx — Production-ready contact page for Sanjivani DevWorks
 * Backend: Convex mutation (convex/contact.ts → api.contact.submitContactForm)
 * Design: Sapphire Veil palette, Instrument Serif / JetBrains Mono / Manrope
 */

import { createElement, useState } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { Mail, MapPin, Clock, Phone, CheckCircle2 } from 'lucide-react';
import FormField from '../ui/FormField';
import SuccessState from '../ui/SuccessState';

/* ─── Scoped styles ────────────────────────────────────────────────────────── */
const STYLES = `
  /* ── Section shell ── */
  .cs-section {
    padding: clamp(5rem, 10vw, 8rem) 0;
    position: relative;
    z-index: 10;
  }

  .cs-shell {
    width: min(1220px, calc(100% - 4rem));
    margin: 0 auto;
  }

  /* ── Grid ── */
  .cs-grid {
    display: grid;
    grid-template-columns: 2fr 3fr;
    gap: clamp(2rem, 4vw, 4rem);
    align-items: start;
  }

  @media (max-width: 900px) {
    .cs-grid {
      grid-template-columns: 1fr;
      gap: 2.5rem;
    }
  }

  /* ── LEFT column ── */
  .cs-left {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    position: sticky;
    top: 7rem;
  }

  @media (max-width: 900px) {
    .cs-left { position: static; }
  }

  .cs-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #00e5ff;
    margin: 0 0 0.6rem;
  }

  .cs-headline {
    font-family: 'Instrument Serif', 'Georgia', serif;
    font-size: clamp(2rem, 3.5vw, 2.8rem);
    line-height: 1.08;
    color: var(--text-strong, #f3fbff);
    margin: 0 0 0.75rem;
    letter-spacing: -0.025em;
  }

  .cs-subtext {
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.95rem;
    line-height: 1.75;
    color: rgba(185, 210, 228, 0.6);
    margin: 0;
  }

  /* ── Contact detail rows ── */
  .cs-details {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    margin-top: 0.5rem;
  }

  .cs-detail {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    transition: border-color 0.22s ease;
  }

  .cs-detail:hover {
    border-color: rgba(0,229,255,0.18);
  }

  .cs-detail-icon {
    color: #00e5ff;
    flex-shrink: 0;
  }

  .cs-detail-body {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .cs-detail-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(185,210,228,0.45);
  }

  .cs-detail-value {
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.9rem;
    color: rgba(185,210,228,0.9);
    font-weight: 500;
  }

  .cs-detail-link {
    color: inherit;
    text-decoration: none;
  }

  .cs-detail-link:hover,
  .cs-detail-link:focus-visible {
    color: var(--text-strong, #f3fbff);
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }

  /* ── Availability card ── */
  .cs-avail {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.9rem 1.1rem;
    border-radius: 12px;
    background: rgba(0,229,255,0.04);
    border: 1px solid rgba(0,229,255,0.16);
  }

  .cs-avail-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 8px rgba(34,197,94,0.7);
    flex-shrink: 0;
    animation: cs-pulse-dot 2.2s ease-in-out infinite;
  }

  @keyframes cs-pulse-dot {
    0%,100% { opacity:1; transform:scale(1); box-shadow:0 0 8px rgba(34,197,94,0.7); }
    50%      { opacity:.65; transform:scale(.75); box-shadow:0 0 4px rgba(34,197,94,0.4); }
  }

  .cs-avail-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: rgba(0,229,255,0.8);
    letter-spacing: 0.06em;
  }

  /* ── RIGHT column — form card ── */
  .cs-card {
    background:
      linear-gradient(160deg, rgba(255,255,255,0.055), rgba(255,255,255,0.018));
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 20px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    padding: clamp(1.5rem, 3vw, 2.2rem);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    overflow: hidden;
    position: relative;
  }

  .cs-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 70% 0%, rgba(0,229,255,0.06) 0%, transparent 55%);
    pointer-events: none;
  }

  .cs-card-head {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .cs-card-title {
    font-family: 'Instrument Serif', 'Georgia', serif;
    font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    color: var(--text-strong, #f3fbff);
    margin: 0;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  .cs-card-sub {
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.88rem;
    color: rgba(185,210,228,0.5);
    margin: 0;
    line-height: 1.6;
  }

  /* ── Error banner ── */
  .cs-error-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.9rem 1rem;
    border-radius: 10px;
    background: rgba(255,80,80,0.08);
    border: 1px solid rgba(255,80,80,0.28);
    font-family: 'Manrope', 'Inter', sans-serif;
    font-size: 0.88rem;
    color: rgba(255,160,160,0.9);
    line-height: 1.5;
  }

  /* ── Form fields ── */
  .cs-form {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
  }

  .cs-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  @media (max-width: 600px) {
    .cs-form-row { grid-template-columns: 1fr; }
  }

  /* ── Submit button ── */
  .cs-submit {
    width: 100%;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    border: none;
    background: linear-gradient(115deg, #00c2d1 0%, #00e5ff 55%, #7c3aed 100%);
    color: #020609;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    transition: opacity 0.2s ease, transform 0.18s ease, box-shadow 0.22s ease;
    margin-top: 0.4rem;
    position: relative;
    overflow: hidden;
  }

  .cs-submit::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(255,255,255,0);
    transition: background 0.2s ease;
  }

  .cs-submit:hover:not(:disabled)::after {
    background: rgba(255,255,255,0.08);
  }

  .cs-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 30px -8px rgba(0,229,255,0.45);
  }

  .cs-submit:active:not(:disabled) {
    transform: translateY(0px);
  }

  .cs-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* ── Spinner ── */
  .cs-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(2,6,9,0.35);
    border-top-color: #020609;
    border-radius: 50%;
    animation: cs-spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes cs-spin {
    to { transform: rotate(360deg); }
  }

  /* ── Light theme overrides ── */
  [data-theme="light"] .cs-eyebrow {
    color: var(--accent);
  }

  [data-theme="light"] .cs-headline {
    color: var(--text-strong);
  }

  [data-theme="light"] .cs-subtext,
  [data-theme="light"] .cs-card-sub {
    color: var(--text-soft);
  }

  [data-theme="light"] .cs-detail {
    background: rgba(255,255,255,0.84);
    border-color: rgba(13,148,136,0.14);
    box-shadow: 0 12px 32px -24px rgba(15,23,42,0.18);
  }

  [data-theme="light"] .cs-detail:hover {
    border-color: rgba(13,148,136,0.28);
    box-shadow: 0 18px 38px -26px rgba(13,148,136,0.18);
  }

  [data-theme="light"] .cs-detail-icon {
    color: var(--accent);
  }

  [data-theme="light"] .cs-detail-label {
    color: var(--text-muted);
  }

  [data-theme="light"] .cs-detail-value {
    color: var(--text-strong);
  }

  [data-theme="light"] .cs-detail-link:hover,
  [data-theme="light"] .cs-detail-link:focus-visible {
    color: var(--accent);
  }

  [data-theme="light"] .cs-avail {
    background: rgba(255,255,255,0.8);
    border-color: rgba(13,148,136,0.16);
    box-shadow: 0 14px 36px -28px rgba(13,148,136,0.16);
  }

  [data-theme="light"] .cs-avail-text {
    color: var(--accent);
  }

  [data-theme="light"] .cs-card {
    background: linear-gradient(180deg, rgba(255,255,255,0.92), rgba(248,250,252,0.88));
    border-color: rgba(13,148,136,0.14);
    box-shadow: 0 24px 64px -42px rgba(15,23,42,0.22);
  }

  [data-theme="light"] .cs-card::before {
    background: radial-gradient(ellipse at 70% 0%, rgba(13,148,136,0.08) 0%, transparent 55%);
  }

  [data-theme="light"] .cs-card-title {
    color: var(--text-strong);
  }

  [data-theme="light"] .cs-error-banner {
    background: rgba(255,235,235,0.92);
    border-color: rgba(239,68,68,0.24);
    color: #991b1b;
  }

  /* ── Submit button pulse ── */
  @keyframes cs-btn-pulse {
    0%, 100% {
      box-shadow: 0 8px 30px -8px rgba(0,229,255,0.3), 0 0 0 0px rgba(0,229,255,0.25);
    }
    50% {
      box-shadow: 0 8px 30px -8px rgba(0,229,255,0.5), 0 0 0 10px rgba(0,229,255,0);
    }
  }

  .cs-submit:not(:disabled) {
    animation: cs-btn-pulse 2.5s ease-in-out infinite;
  }

  .cs-submit:disabled {
    animation: none;
  }

  /* ── Input / textarea focus glow (override FormField defaults within form) ── */
  .cs-form .ff-input:focus,
  .cs-form .ff-textarea:focus {
    box-shadow: 0 0 0 2px rgba(151, 246, 255, 0.4);
    border-color: rgba(151, 246, 255, 0.6);
  }

  [data-theme="light"] .cs-form .ff-input:focus,
  [data-theme="light"] .cs-form .ff-textarea:focus {
    box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.35);
    border-color: rgba(13, 148, 136, 0.5);
  }
`;

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || document.getElementById('cs-styles')) return;
  const tag = document.createElement('style');
  tag.id = 'cs-styles';
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  stylesInjected = true;
}

/* ─── Constants ────────────────────────────────────────────────────────────── */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_CONTACT = {
  title: 'Let us build something remarkable together.',
  description:
    'We stay intentionally selective so every engagement gets founder-level attention. Share what you are building and we will come back with honest guidance on fit, scope, and next steps.',
  email: 'sanjivanidevworks@gmail.com',
  emailHref: 'mailto:sanjivanidevworks@gmail.com',
  phone: '+91 88662 25485',
  phoneHref: 'tel:+918866225485',
  location: 'Ahmedabad, Gujarat, India',
  availability: 'Currently available for select client engagements in 2026',
  availabilityShort: 'Available for select projects in 2026',
};

const EASE = [0.22, 1, 0.36, 1];

/* ─── Component ─────────────────────────────────────────────────────────────*/
export default function ContactSection({ contact = DEFAULT_CONTACT, reducedMotion }) {
  injectStyles();

  const contactContent = { ...DEFAULT_CONTACT, ...contact };
  const contactDetails = [
    {
      icon: Mail,
      label: 'Email',
      value: contactContent.email,
      href: contactContent.emailHref,
    },
    {
      icon: Phone,
      label: 'Phone',
      value: contactContent.phone,
      href: contactContent.phoneHref,
    },
    {
      icon: MapPin,
      label: 'Location',
      value: contactContent.location,
    },
    {
      icon: Clock,
      label: 'Availability',
      value: contactContent.availability,
    },
  ];

  /* Form state */
  const EMPTY_FORM = { name: '', email: '', subject: '', message: '', website: '' };
  const [form, setForm]               = useState(EMPTY_FORM);
  const [errors, setErrors]           = useState({});
  const [status, setStatus]           = useState('idle');   // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  /* ── Client-side validation ── */
  function validate() {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = 'Name must be at least 2 characters.';
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim()))
      e.email = 'Please enter a valid email address.';
    if (!form.subject.trim() || form.subject.trim().length < 3)
      e.subject = 'Subject must be at least 3 characters.';
    if (!form.message.trim() || form.message.trim().length < 20)
      e.message = 'Message must be at least 20 characters.';
    if (form.message.trim().length > 2000)
      e.message = 'Message must be under 2000 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  /* ── Field change ── */
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear individual error on change
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  }

  /* ── Reset ── */
  function handleReset() {
    setStatus('idle');
    setErrors({});
    setErrorMessage('');
    setForm(EMPTY_FORM);
  }

  return (
    <section id="contact" className="cs-section">
      <div className="cs-shell">
        <div className="cs-grid">

          {/* ── LEFT: copy + details ── */}
          <Motion.div
            className="cs-left"
            initial={reducedMotion ? undefined : { opacity: 0, x: -24 }}
            whileInView={reducedMotion ? undefined : { opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <div>
              <p className="cs-eyebrow">Start a Project</p>
              <h2 className="cs-headline">
                {contactContent.title}
              </h2>
              <p className="cs-subtext">
                {contactContent.description}
              </p>
            </div>

            {/* Contact detail rows */}
            <div className="cs-details">
              {contactDetails.map(({ icon, label, value, href }) => (
                <div className="cs-detail" key={label}>
                  {createElement(icon, {
                    size: 16,
                    className: 'cs-detail-icon',
                    'aria-hidden': true,
                  })}
                  <div className="cs-detail-body">
                    <span className="cs-detail-label">{label}</span>
                    {href ? (
                      <a className="cs-detail-value cs-detail-link" href={href}>
                        {value}
                      </a>
                    ) : (
                      <span className="cs-detail-value">{value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Availability pill */}
            <div className="cs-avail" role="status" aria-live="polite">
              <span className="cs-avail-dot" aria-hidden="true" />
              <span className="cs-avail-text">{contactContent.availabilityShort}</span>
            </div>
          </Motion.div>

          {/* ── RIGHT: form card ── */}
          <Motion.div
            initial={reducedMotion ? undefined : { opacity: 0, y: 28 }}
            whileInView={reducedMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
          >
            <div className="cs-card">

              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  /* ── Success state ── */
                  <Motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SuccessState onReset={handleReset} />
                  </Motion.div>
                ) : (
                  /* ── Form ── */
                  <Motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Card header */}
                    <div className="cs-card-head">
                      <h3 className="cs-card-title">Send us a message</h3>
                      <p className="cs-card-sub">
                        We reply to every inquiry personally — typically within one business day.
                      </p>
                    </div>

                    {/* Error banner */}
                    <AnimatePresence>
                      {status === 'error' && errorMessage && (
                        <Motion.div
                          className="cs-error-banner"
                          role="alert"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          ⚠ {errorMessage}
                        </Motion.div>
                      )}
                    </AnimatePresence>

                    {/* Form fields */}
                    <form className="cs-form" onSubmit={handleSubmit} noValidate>

                      {/* Honeypot — hidden from humans, bots fill it */}
                      <input
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ display: 'none' }}
                      />

                      {/* Name + Email row */}
                      <div className="cs-form-row">
                        <FormField
                          label="Full Name"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          error={errors.name}
                          placeholder="Arjun Patel"
                        />
                        <FormField
                          label="Email Address"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          error={errors.email}
                          placeholder="you@company.com"
                        />
                      </div>

                      {/* Subject */}
                      <FormField
                        label="Subject"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        error={errors.subject}
                        placeholder="We'd like to build a SaaS dashboard"
                      />

                      {/* Message textarea */}
                      <FormField
                        label="Message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        error={errors.message}
                        placeholder="Tell us about your product, goals, timeline, and where you need support. The more detail, the better."
                        rows={5}
                        maxLength={2000}
                      />

                      {/* Submit */}
                      <button
                        type="submit"
                        className="cs-submit"
                        disabled={status === 'loading'}
                        aria-busy={status === 'loading'}
                      >
                        {status === 'loading' ? (
                          <>
                            <span className="cs-spinner" aria-hidden="true" />
                            Sending…
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={15} aria-hidden="true" />
                            Send Message
                          </>
                        )}
                      </button>

                    </form>
                  </Motion.div>
                )}
              </AnimatePresence>

            </div>
          </Motion.div>

        </div>
      </div>
    </section>
  );
}
